import {
  validateCharacterAndDigit,
  validateCharacterAndSpace,
  validateCharacterSpaceAndDigit,
  validateCity,
  validateCompany,
  validatePayrate,
  validatePosition,
  validateURL,
  Validators,
} from "./Validator";
import { ProfanitiesWordKeysToReplace } from "./Helper";

/**
 * PostJob Validation Helper
 * Contains all validation logic for the PostJob component
 */
export class PostJobValidator {
  constructor(profanitiesWords = []) {
    this.profanitiesWords = profanitiesWords;
  }

  /**
   * Validates content against profanities list
   * @param {string} value - The value to validate
   * @returns {boolean} - True if content contains profanity, false otherwise
   */
  validateContent(value) {
    if (value == null) return false;

    let data = value.toLowerCase();
    ProfanitiesWordKeysToReplace.forEach((item) => {
      data = data.replaceAll(item, "");
    });
    data = data.replace(/\s/g, "~");
    let list = data.split("~");

    return list.some((w) => this.profanitiesWords.includes(w));
  }

  /**
   * Validates individual field on blur
   * @param {string} key - Field name
   * @param {string} value - Field value
   * @param {object} state - Current component state
   * @returns {object} - Validation results and state updates
   */
  validateField(key, value, state) {
    const updates = {};
    let hasError = false;

    switch (key) {
      case "payRate":
        const payval = validatePayrate(value, "payRate");
        if (payval.error === true) {
          updates.payRateError = true;
          hasError = true;
        } else {
          updates.validCompensationContentError = this.validateContent(value);
          updates.payRateError = false;
        }
        if (value === "") {
          updates.payRateError = false;
        }
        break;

      case "company":
        if (value === "") {
          updates.companyError = state.jobTypeId !== "3";
        } else {
          const val = validateCompany(value, "company");
          if (val.error === true) {
            updates.companyNameValidError = true;
            hasError = true;
          } else {
            updates.validCompanyContentError = this.validateContent(value);
            updates.companyNameValidError = false;
          }
          updates.companyError = false;
        }
        break;

      case "city":
        if (value === "") {
          updates.cityError = true;
          hasError = true;
        } else {
          const val = validateCity(value, "city");
          if (val.error === true) {
            updates.cityValidError = true;
            hasError = true;
          } else {
            updates.validCityContentError = this.validateContent(value);
            updates.cityValidError = false;
          }
          updates.cityError = false;
        }
        break;

      case "stateName":
        if (value === "") {
          updates.countryStateError = true;
          hasError = true;
        } else {
          const val = validateCharacterAndSpace(value, "state");
          if (val.error === true) {
            updates.stateValidError = true;
            updates.countryStateError = false;
            hasError = true;
          } else {
            updates.validStateContentError = this.validateContent(value);
            updates.stateValidError = false;
          }
          updates.countryStateError = false;
        }
        break;

      case "contactName":
        const isJobType3 = state.jobTypeId === "3";
        if (value === "") {
          updates.contactNameError = !isJobType3;
        } else {
          const val = Validators.isName(value, "firstname");
          if (val.error === true) {
            updates.nameValidError = true;
            hasError = true;
          } else {
            updates.validContactContentError = this.validateContent(value);
            updates.nameValidError = false;
          }
          updates.contactNameError = false;
        }
        break;

      case "position":
        if (value === "") {
          updates.positionError = true;
          hasError = true;
        } else {
          const val = validatePosition(value, "position");
          if (val.error === true) {
            updates.positionValidError = true;
            hasError = true;
          } else {
            updates.validPositionContentError = this.validateContent(value);
            updates.positionValidError = false;
          }
          updates.positionError = false;
        }
        break;

      case "contactEmail":
        if (state.jobTypeId !== "3") {
          if (value === "") {
            updates.emailError = true;
            hasError = true;
          } else {
            const val = Validators.email(value, "email");
            if (val.error === true) {
              updates.emailValidError = true;
              hasError = true;
            } else {
              updates.emailValidError = false;
            }
            updates.emailError = false;
          }
        }
        break;

      case "description":
        if (state.jobTypeId !== "3") {
          if (value === "") {
            updates.descriptionError = true;
            hasError = true;
          } else {
            updates.validDescriptionContentError = this.validateContent(value);
            updates.descriptionError = false;
          }
        }
        break;

      case "address1":
        if (value === "") {
          updates.addressError = true;
          hasError = true;
        } else {
          updates.validAddress1ContentError = this.validateContent(value);
          updates.addressError = false;
        }
        break;

      case "address2":
        updates.validAddress2ContentError = this.validateContent(value);
        break;

      case "mQualification":
        updates.validMinimumQualContentError = this.validateContent(value);
        break;

      case "responsibilities":
        updates.validPositionResContentError = this.validateContent(value);
        break;

      case "pQualification":
        updates.validPreferredQualContentError = this.validateContent(value);
        break;

      case "policy":
        updates.validEmpPolicyContentError = this.validateContent(value);
        break;

      case "telephone":
        const phoneVal = Validators.isPhone(value, "phone");
        if (phoneVal.error === true) {
          updates.phoneValidError = true;
          hasError = true;
        } else {
          updates.phoneValidError = false;
        }
        if (value === "") {
          updates.phoneValidError = false;
        }
        break;

      case "zip":
        const zipVal = validateCharacterAndDigit(value, "zip");
        if (zipVal.error === true) {
          updates.zipValidError = true;
          hasError = true;
        } else {
          updates.validZipContentError = this.validateContent(value);
          updates.zipValidError = false;
        }
        if (value === "") {
          updates.zipValidError = false;
        }
        break;

      case "postLink":
        if (state.jobTypeId === "3") {
          if (value === "") {
            updates.linkError = true;
            updates.linkValidError = false;
            hasError = true;
          } else {
            updates.validPostLinkContentError = this.validateContent(value);
            updates.linkError = false;
            updates.linkValidError = false;
          }
        }
        break;

      default:
        break;
    }

    return { updates, hasError };
  }

  /**
   * Validates post link URL
   * @param {string} link - The URL to validate
   * @returns {object} - Validation result
   */
  validateLink(link) {
    const val = validateURL(link, "postlink");
    return {
      isValid: !val.error,
      error: val.error,
    };
  }

  /**
   * Main validation function for form submission
   * @param {object} formData - All form data
   * @param {object} state - Current component state
   * @returns {object} - Validation result and state updates
   */
  validate(formData, state) {
    const {
      jobTypeId,
      startDate,
      endDate,
      company,
      group,
      address1,
      country,
      countryState,
      city,
      contactName,
      contactEmail,
      position,
      description,
      travelRequired,
      telephone,
      zip,
      jobLength,
      industry,
      postLink,
      payRate,
      showStateTextBox,
    } = formData;

    let result = true;
    const stateUpdates = {};

    // Job Type validation
    if (Validators.isUndefined(jobTypeId, "jobTypeId")) {
      result = false;
      stateUpdates.jobTypeIdError = true;
      stateUpdates.resultState = false;
    }

    // Date validations
    if (Validators.isUndefined(startDate, "startDate")) {
      result = false;
      stateUpdates.startDateError = true;
    } else {
      stateUpdates.startDateError = false;
    }

    if (Validators.isUndefined(endDate, "endDate")) {
      result = false;
      stateUpdates.endDateError = true;
    } else {
      stateUpdates.endDateError = false;
    }

    // Company validation
    if (jobTypeId !== "3") {
      if (Validators.isNull(company)) {
        result = false;
        stateUpdates.companyError = true;
      } else {
        const val = validateCompany(company, "company");
        if (val.error === true) {
          result = false;
          stateUpdates.companyNameValidError = true;
        } else {
          stateUpdates.companyNameValidError = false;
        }
        stateUpdates.companyError = false;
      }
    } else {
      if (company === "" || Validators.isUndefined(company)) {
        stateUpdates.companyNameValidError = false;
      } else {
        const val = validateCompany(company, "company");
        if (val.error === true) {
          result = false;
          stateUpdates.companyNameValidError = true;
          stateUpdates.companyError = false;
        } else {
          stateUpdates.companyNameValidError = false;
        }
        stateUpdates.companyError = false;
      }
    }

    // Group validation
    if (
      Validators.isUndefined(group) ||
      group === 0 ||
      group === "" ||
      group[0] === 0
    ) {
      result = false;
      stateUpdates.groupError = true;
    } else {
      stateUpdates.groupError = false;
    }

    // Address validation
    if (Validators.isUndefined(address1) || address1 === "") {
      result = false;
      stateUpdates.addressError = true;
    } else {
      stateUpdates.addressError = false;
    }

    // Country validation
    if (jobTypeId !== "3") {
      if (Validators.isUndefined(country) || country === 0 || country === "0") {
        result = false;
        stateUpdates.countryError = true;
      } else {
        stateUpdates.countryError = false;
      }
    } else {
      stateUpdates.countryError = false;
    }

    // State validation
    if (!showStateTextBox) {
      if (jobTypeId !== "3") {
        if (
          Validators.isUndefined(countryState) ||
          countryState === "" ||
          countryState === 0
        ) {
          result = false;
          stateUpdates.countryStateError = true;
        } else {
          const val = validateCharacterSpaceAndDigit(countryState, "state");
          if (val.error === true) {
            result = false;
            stateUpdates.stateValidError = true;
            stateUpdates.countryStateError = false;
          } else {
            stateUpdates.stateValidError = false;
          }
          stateUpdates.countryStateError = false;
        }
      } else {
        if (countryState === "" || Validators.isUndefined(countryState)) {
          stateUpdates.stateValidError = false;
        } else {
          const val = validateCharacterSpaceAndDigit(countryState, "state");
          if (val.error === true) {
            result = false;
            stateUpdates.stateValidError = true;
            stateUpdates.countryStateError = false;
          } else {
            stateUpdates.stateValidError = false;
          }
          stateUpdates.countryStateError = false;
        }
      }
    } else {
      if (jobTypeId !== "3") {
        if (
          Validators.isUndefined(countryState) ||
          countryState === "" ||
          countryState === 0
        ) {
          result = false;
          stateUpdates.countryStateError = true;
        } else {
          const val = validateCharacterAndSpace(countryState, "state");
          if (val.error === true) {
            result = false;
            stateUpdates.stateValidError = true;
            stateUpdates.countryStateError = false;
          } else {
            stateUpdates.stateValidError = false;
          }
          stateUpdates.countryStateError = false;
        }
      } else {
        if (countryState === "" || Validators.isUndefined(countryState)) {
          stateUpdates.stateValidError = false;
        } else {
          const val = validateCharacterAndSpace(countryState, "state");
          if (val.error === true) {
            result = false;
            stateUpdates.stateValidError = true;
            stateUpdates.countryStateError = false;
          } else {
            stateUpdates.stateValidError = false;
          }
          stateUpdates.countryStateError = false;
        }
      }
    }

    // City validation
    if (jobTypeId !== "3") {
      if (Validators.isUndefined(city)) {
        result = false;
        stateUpdates.cityError = true;
      } else {
        const val = validateCity(city, "city");
        if (val.error === true) {
          result = false;
          stateUpdates.cityValidError = true;
        } else {
          stateUpdates.cityValidError = false;
        }
        stateUpdates.cityError = false;
      }
    } else {
      if (city === "" || Validators.isUndefined(city)) {
        stateUpdates.cityValidError = false;
      } else {
        const val = validateCity(city, "city");
        if (val.error === true) {
          result = false;
          stateUpdates.cityValidError = true;
          stateUpdates.cityError = false;
        } else {
          stateUpdates.cityValidError = false;
        }
        stateUpdates.cityError = false;
      }
    }

    // Contact name validation
    if (jobTypeId !== "3") {
      if (Validators.isUndefined(contactName)) {
        result = false;
        stateUpdates.contactNameError = true;
      } else {
        const val = Validators.isName(contactName, "firstname");
        if (val.error === true) {
          result = false;
          stateUpdates.nameValidError = true;
          stateUpdates.contactNameError = false;
        } else {
          stateUpdates.nameValidError = false;
        }
        stateUpdates.contactNameError = false;
      }
    } else {
      if (contactName === "" || Validators.isUndefined(contactName)) {
        stateUpdates.contactNameError = false;
      } else {
        const val = Validators.isName(contactName, "contactName");
        if (val.error === true) {
          result = false;
          stateUpdates.nameValidError = true;
          stateUpdates.contactNameError = false;
        } else {
          stateUpdates.nameValidError = false;
        }
        stateUpdates.contactNameError = false;
      }
    }

    // Contact email validation
    if (jobTypeId !== "3") {
      if (Validators.isUndefined(contactEmail)) {
        result = false;
        stateUpdates.emailError = true;
      } else {
        const val = Validators.email(contactEmail, "email");
        if (val.error === true) {
          result = false;
          stateUpdates.emailValidError = true;
        } else {
          stateUpdates.emailValidError = false;
        }
        stateUpdates.emailError = false;
      }
    } else {
      if (contactEmail === "" || Validators.isUndefined(contactEmail)) {
        stateUpdates.emailValidError = false;
      } else {
        const val = Validators.email(contactEmail, "contactEmail");
        if (val.error === true) {
          result = false;
          stateUpdates.emailValidError = true;
          stateUpdates.emailError = false;
        } else {
          stateUpdates.emailValidError = false;
        }
        stateUpdates.emailError = false;
      }
    }

    // Position validation
    if (Validators.isUndefined(position)) {
      result = false;
      stateUpdates.positionError = true;
    } else {
      const val = validatePosition(position, "position");
      if (val.error === true) {
        result = false;
        stateUpdates.positionValidError = true;
      } else {
        stateUpdates.positionValidError = false;
      }
      stateUpdates.positionError = false;
    }

    // Description validation
    if (jobTypeId !== "3") {
      if (Validators.isUndefined(description)) {
        result = false;
        stateUpdates.descriptionError = true;
      } else {
        stateUpdates.descriptionError = false;
      }
    }

    // Travel validation
    if (jobTypeId !== "3") {
      if (Validators.isUndefined(travelRequired) || travelRequired === "") {
        result = false;
        stateUpdates.travelError = true;
      } else {
        stateUpdates.travelError = false;
      }
    } else {
      if (Validators.isUndefined(travelRequired) || travelRequired === "") {
        stateUpdates.travelError = false;
      }
    }

    // Phone validation
    if (telephone === undefined || telephone === "") {
      stateUpdates.phoneValidError = false;
    } else {
      const val = Validators.isPhone(telephone, "telephone");
      if (val.error === true) {
        result = false;
        stateUpdates.phoneValidError = true;
      } else {
        stateUpdates.phoneValidError = false;
      }
    }

    // Zip validation
    const zipval = validateCharacterAndDigit(zip, "zip");
    if (zipval.error === true) {
      result = false;
      stateUpdates.zipValidError = true;
    } else {
      stateUpdates.zipValidError = false;
    }
    if (zip === "") {
      stateUpdates.zipValidError = false;
    }

    // Job length validation
    if (jobTypeId !== "3") {
      if (Validators.isUndefined(jobLength) || jobLength === "") {
        result = false;
        stateUpdates.joblengthError = true;
      } else {
        stateUpdates.joblengthError = false;
      }
    } else {
      if (Validators.isUndefined(jobLength) || jobLength === "") {
        stateUpdates.joblengthError = false;
      }
    }

    // Industry validation
    if (
      Validators.isUndefined(industry) ||
      industry === "" ||
      industry === "0"
    ) {
      result = false;
      stateUpdates.industryError = true;
    } else {
      stateUpdates.industryError = false;
    }

    // Post link validation for job type 3
    if (jobTypeId === "3") {
      if (Validators.isUndefined(postLink) || postLink === "") {
        result = false;
        stateUpdates.linkError = true;
      } else {
        stateUpdates.linkError = false;
      }
    }

    // Pay rate validation
    const payval = validatePayrate(payRate, "payRate");
    if (payval.error === true) {
      result = false;
      stateUpdates.payRateError = true;
    } else {
      stateUpdates.payRateError = false;
    }
    if (payRate === "") {
      stateUpdates.payRateError = false;
    }

    // Check for content validation errors
    const contentErrors = [
      "validCompanyContentError",
      "validAddress1ContentError",
      "validAddress2ContentError",
      "validCityContentError",
      "validStateContentError",
      "validZipContentError",
      "validContactContentError",
      "validPositionContentError",
      "validCompensationContentError",
      "validDescriptionContentError",
      "validPositionResContentError",
      "validMinimumQualContentError",
      "validPreferredQualContentError",
      "validEmpPolicyContentError",
      "validPostLinkContentError",
    ];

    const hasContentErrors = contentErrors.some((error) => state[error]);
    if (hasContentErrors) {
      result = false;
    }

    // Final validation for job type specific requirements
    if (jobTypeId !== "3") {
      const requiredFields = [
        jobTypeId === undefined,
        state.nameValidError === true,
        startDate === undefined,
        endDate === undefined,
        company === undefined,
        group === undefined || group === "" || group === 0 || group[0] === 0,
        address1 === undefined,
        country === undefined || country === 0 || country === "0",
        countryState === undefined || countryState === 0 || countryState === "",
        state.stateValidError === true,
        city === undefined,
        contactName === undefined,
        state.cityValidError === true,
        state.zipValidError === true,
        state.payRateError === true,
        state.phoneValidError === true,
        contactEmail === undefined,
        position === undefined,
        state.positionValidError === true,
        description === undefined,
        travelRequired === undefined || travelRequired === "",
        jobLength === undefined || jobLength === "",
        industry === undefined || industry === "0",
        state.companyNameValidError === true,
      ];

      if (requiredFields.some((field) => field)) {
        result = false;
      }
    } else {
      const requiredFieldsType3 = [
        jobTypeId === undefined,
        state.nameValidError === true,
        startDate === undefined,
        endDate === undefined,
        state.payRateError === true,
        group === undefined || group === 0 || group === "" || group[0] === 0,
        state.phoneValidError === true,
        state.cityValidError === true,
        state.stateValidError === true,
        state.zipValidError === true,
        address1 === undefined,
        position === undefined,
        state.positionValidError === true,
        industry === undefined || industry === "0",
        state.companyNameValidError === true,
        postLink === undefined || postLink === "",
      ];

      if (requiredFieldsType3.some((field) => field)) {
        result = false;
      }
    }

    return {
      isValid: result,
      stateUpdates,
    };
  }
}

// Export a default instance for convenience
export const postJobValidator = new PostJobValidator();

// Export the class for cases where a new instance is needed
export default PostJobValidator;
